import { useState, useMemo } from "react";
import { ArrowLeft, Search, ArrowUp, ArrowDown } from "lucide-react";
import { useTranslation } from "@/lib/i18n.jsx";

const CurrencySelector = ({
  isOpen,
  onClose,
  onSelect,
  items,
  type = "currency", // 'currency' or 'crypto'
  selectedItem,
  cryptoPrices,
  cryptoChanges,
  selectedCurrency
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const getPrice = (item) => {
    if (!cryptoPrices) return item.price;

    // ✅ direct mapping (same as MainForm)
    return cryptoPrices?.[item.code] || item.price;
  };

  const getChange = (item) => {
    const change = cryptoChanges?.[item.code];
    return typeof change === "number" && !Number.isNaN(change) ? change : null;
  };

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;

    const query = searchQuery.toLowerCase().trim();
    return items.filter(item => {
      const code = item.code?.toLowerCase() || "";
      const name = item.name?.toLowerCase() || "";
      const symbol = item.symbol?.toLowerCase() || "";

      return code.includes(query) || name.includes(query) || symbol.includes(query);
    });
  }, [items, searchQuery]);

  const handleSelect = (item) => {
    onSelect(item);
    setSearchQuery("");
    onClose();
  };

  const handleClose = () => {
    setSearchQuery("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-white dark:bg-gray-900 rounded-2xl overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-center px-4 py-4 border-b border-gray-100 dark:border-gray-800 relative flex-shrink-0">
        <button
          onClick={handleClose}
          className="absolute left-4 p-2 -ml-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          aria-label={t('widget.currency_selector.go_back', 'Go back')}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
          {type === "currency" ? t('widget.currency_selector.select_currency', 'Select Currency') : t('widget.currency_selector.select_crypto', 'Select Crypto')}
        </h2>
      </div>

      {/* Search Bar */}
      <div className="px-4 py-3 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={type === "currency" ? t('widget.currency_selector.type_currency_placeholder', 'Type a currency') : t('widget.currency_selector.type_crypto_placeholder', 'Type a crypto')}
            className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all text-sm"
            autoFocus
          />
        </div>
      </div>

      {/* List Header */}
      <div className="px-4 py-2 flex-shrink-0">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
          {type === "currency" ? t('widget.currency_selector.currencies', 'Currencies') : t('widget.currency_selector.cryptocurrencies', 'Cryptocurrencies')}
        </span>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {filteredItems.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
            {type === "currency"
              ? t('widget.currency_selector.no_currencies_found', 'No currencies found')
              : t('widget.currency_selector.no_cryptocurrencies_found', 'No cryptocurrencies found')}
          </div>
        ) : (
          <div className="space-y-0.5">
            {filteredItems.map((item, index) => {
              const isSelected = type === "currency"
                ? selectedItem?.code === item.code
                : selectedItem?.code === item.code && selectedItem?.network === item.network;
              const code = item.code || item.symbol;
              const uniqueKey = type === "crypto" ? `${item.code}-${item.network}-${index}` : item.code;

              return (
                <button
                  key={uniqueKey}
                  onClick={() => handleSelect(item)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${isSelected
                    ? "bg-sky-50 dark:bg-sky-900/20"
                    : "hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                >
                  {/* Flag or Icon */}
                  <div className="flex-shrink-0 w-8 h-6 flex items-center justify-center">
                    {type === "currency" && item.countryCode ? (
                      <img
                        src={`https://flagcdn.com/w40/${item.countryCode.toLowerCase()}.png`}
                        alt={item.code}
                        className="w-6 h-4 object-cover"
                      />
                    ) : type === "crypto" && item.icon ? (
                      <span className="text-xl font-bold text-gradient">
                        {item.icon}
                      </span>
                    ) : (
                      <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400">
                        {code?.charAt(0)}
                      </div>
                    )}
                  </div>


                  {/* Code */}
                  <span className={`font-semibold text-sm min-w-[50px] text-left ${isSelected
                    ? "text-gredient"
                    : "text-gray-900 dark:text-white"
                    }`}>
                    {code}
                  </span>

                  {/* Name and Network */}
                  <div className="flex-1 text-left">
                    <span className="text-sm text-gray-500 dark:text-gray-400 truncate block">
                      {item.name}
                    </span>
                    {type === "crypto" && item.network && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">
                        {item.network}
                      </span>
                    )}
                  </div>

                  {/* Price + 24h change for crypto */}
                  {type === "crypto" && (
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-xs font-medium text-gray-400">
                        {selectedCurrency?.symbol}
                        {getPrice(item)?.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}
                      </span>
                      {getChange(item) !== null && (
                        <span
                          className={`flex items-center gap-0.5 text-[11px] font-semibold ${getChange(item) >= 0
                            ? "text-emerald-500"
                            : "text-red-500"
                            }`}
                        >
                          {getChange(item) >= 0 ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )}
                          {Math.abs(getChange(item)).toFixed(2)}%
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencySelector;
